// ---------- Save template dialog ----------

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import { FormsTemplateStatus } from '@commenda/types';
import { CreateKeyModal } from '../../template-keys/CreateKeyModal';
import { EditableKeyCell } from '../../templates/EditableKeyCell';
import { EditableStatusCell } from '../../templates/EditableStatusCell';
import type { TemplateMetadata } from '@commenda/types';

type SaveDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  metadata: TemplateMetadata;
  saveDialogData: Partial<TemplateMetadata>;
  setSaveDialogData: React.Dispatch<
    React.SetStateAction<Partial<TemplateMetadata>>
  >;
  onSave: () => void;
};

export default function SaveTemplateDialog({
  open,
  onOpenChange,
  metadata,
  saveDialogData,
  setSaveDialogData,
  onSave,
}: SaveDialogProps) {
  const isCreateKeyModalOpen = useToggle(false);
  return (
    <>
      <CreateKeyModal
        open={isCreateKeyModalOpen.value}
        onOpenChange={isCreateKeyModalOpen.toggle}
        onSuccess={(createdKey) => {
          setSaveDialogData((prev) => ({
            ...prev,
            key: createdKey.key,
            keyName: createdKey.name,
          }));
        }}
      />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader className="flex flex-col gap-xs justify-start items-start">
            <DialogTitle>
              {metadata?.id ? 'Update Template' : 'Save as Template'}
            </DialogTitle>
            <DialogDescription>
              {metadata?.id
                ? 'Update the template details and save your changes.'
                : 'Enter the details for your new form template.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 px-4">
            <div className="space-y-2">
              <Label htmlFor="templateName">Template Name *</Label>
              <Input
                id="templateName"
                value={saveDialogData.name}
                onChange={(e) =>
                  setSaveDialogData((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="Enter template name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="templateDescription">Description</Label>
              <Textarea
                id="templateDescription"
                value={saveDialogData.description}
                onChange={(e) =>
                  setSaveDialogData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Enter template description"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="templateStatus">Status *</Label>
                <EditableStatusCell
                  id={metadata?.id}
                  status={saveDialogData?.status as FormsTemplateStatus}
                  onUpdate={(id: string, newStatus: FormsTemplateStatus) => {
                    setSaveDialogData((prev) => ({
                      ...prev,
                      status: newStatus,
                    }));
                  }}
                  size="md"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="templateKey">Template Key</Label>
                <EditableKeyCell
                  template={{
                    key: saveDialogData?.key ?? metadata?.key,
                    keyName: saveDialogData?.keyName ?? metadata?.keyName,
                    id: metadata?.id,
                  }}
                  onUpdate={async (
                    id: string,
                    newKey: string | null,
                    newKeyName: string | null
                  ) => {
                    setSaveDialogData((prev) => ({
                      ...prev,
                      key: newKey,
                      keyName: newKeyName,
                    }));
                  }}
                  onAddKey={() => {
                    isCreateKeyModalOpen.on();
                  }}
                  size="md"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="templateVersion">Version *</Label>
              <Input
                id="templateVersion"
                value={saveDialogData.version}
                onChange={(e) =>
                  setSaveDialogData((prev) => ({
                    ...prev,
                    version: e.target.value,
                  }))
                }
                placeholder="1.0.0"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="templateVersionNote">Version Note</Label>
              <Textarea
                id="templateVersionNote"
                value={saveDialogData.versionNote}
                onChange={(e) =>
                  setSaveDialogData((prev) => ({
                    ...prev,
                    versionNote: e.target.value,
                  }))
                }
                placeholder="What changed in this version?"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button
              variant="primary"
              onClick={onSave}
              disabled={
                !saveDialogData.name ||
                !saveDialogData.status ||
                !saveDialogData.version
              }
            >
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
